import { create } from 'zustand';

import type { AnalyticsAdapter } from '@/adapters/analytics/types';
import type { DeviceRegistrationBffAdapter } from '@/adapters/device/types';
import type { DevicePlatform } from '@/generated/usermobile-bff/graphql';
import type { Logger } from '@/adapters/logger/types';
import type { PushNotificationAdapter } from '@/adapters/pushNotification/types';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@/constants/analytics';

interface DeviceRegistrationState {
  isRegistering: boolean;
  isRegistered: boolean;
  fcmToken: string | null;
  error: string | null;
}

interface DeviceRegistrationActions {
  requestPermissionAndRegister: () => Promise<void>;
  unregister: () => Promise<void>;
  clearError: () => void;
}

interface DeviceRegistrationAdapters {
  pushNotification: PushNotificationAdapter;
  bff: DeviceRegistrationBffAdapter;
  logger: Logger;
  analytics: AnalyticsAdapter;
  getPlatform: () => DevicePlatform;
}

const createDeviceRegistrationStore = (adapters: DeviceRegistrationAdapters) => {
  const state = { unsubscribeTokenRefresh: null as (() => void) | null };

  return create<DeviceRegistrationState & DeviceRegistrationActions>((set, get) => ({
    isRegistering: false,
    isRegistered: false,
    fcmToken: null,
    error: null,

    requestPermissionAndRegister: async () => {
      set({ isRegistering: true, error: null });
      const { pushNotification, bff, logger, analytics, getPlatform } = adapters;

      try {
        const granted = await pushNotification.requestPermission();

        if (!granted) {
          analytics.track(ANALYTICS_EVENTS.APP_PERMISSION_DENIED, {
            [ANALYTICS_PARAMS.PERMISSION]: 'push_notifications',
          });
          logger.addBreadcrumb('Push notification permission denied');
          set({ isRegistering: false, error: 'pushNotification.errors.permissionDenied' });
          return;
        }

        const token = await pushNotification.getToken();
        const platform = getPlatform();

        logger.addBreadcrumb('Registering device with BFF', { platform });
        await bff.registerDevice(token, platform);
        logger.addBreadcrumb('Device registered with BFF');

        state.unsubscribeTokenRefresh?.();
        state.unsubscribeTokenRefresh = pushNotification.onTokenRefresh((newToken) => {
          logger.addBreadcrumb('FCM token refreshed, re-registering device');
          void bff.registerDevice(newToken, platform).catch((error) => {
            logger.captureException(error);
          });
          set({ fcmToken: newToken });
        });

        set({ isRegistering: false, isRegistered: true, fcmToken: token });
      } catch (error) {
        logger.captureException(error);
        set({ isRegistering: false, error: 'pushNotification.errors.registrationFailed' });
      }
    },

    unregister: async () => {
      const { bff, logger } = adapters;
      const { fcmToken } = get();

      if (!fcmToken) {
        return;
      }

      try {
        state.unsubscribeTokenRefresh?.();
        state.unsubscribeTokenRefresh = null;

        logger.addBreadcrumb('Unregistering device from BFF');
        await bff.unregisterDevice(fcmToken);
        logger.addBreadcrumb('Device unregistered from BFF');

        set({ isRegistered: false, fcmToken: null, error: null });
      } catch (error) {
        logger.captureException(error);
        set({ error: 'pushNotification.errors.unregisterFailed' });
      }
    },

    clearError: () => set({ error: null }),
  }));
};

export { createDeviceRegistrationStore };
export type { DeviceRegistrationAdapters };
